import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX } from 'react-icons/fi';

const TermsOfServiceModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[200] flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden border border-black/10"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-black/10">
            <h2 className="text-2xl font-bold text-[#1a1012]">Terms of Service</h2>
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-[#fbeceb] transition-colors text-[#ba1609]"
            >
              <FiX className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
            <div className="text-[#5b4d4f] space-y-6">
              {/* Last Updated */}
              <div className="text-center">
                <p className="text-sm text-[#5b4d4f]">Last updated: January 2025</p>
              </div>

              {/* Introduction */}
              <section>
                <h3 className="text-xl font-semibold text-[#ba1609] mb-3">Agreement to Terms</h3>
                <p className="leading-relaxed">
                  By using <span style={{ fontFamily: 'Playfair Display, serif' }}>Lasmari Chauffeurs</span>' luxury transportation services in Corfu, Greece, you agree to be bound by these Terms of Service. 
                  Please read them carefully before making a booking.
                </p>
              </section>

              {/* Service Description */}
              <section>
                <h3 className="text-xl font-semibold text-[#ba1609] mb-3">Service Description</h3>
                <p className="leading-relaxed mb-3">
                  <span style={{ fontFamily: 'Playfair Display, serif' }}>Lasmari Chauffeurs</span> provides luxury transportation services including:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Airport transfers to/from Corfu International Airport</li>
                  <li>Hotel and accommodation transfers</li>
                  <li>Sightseeing tours around North Corfu</li>
                  <li>Private transportation services</li>
                  <li>Special event transportation</li>
                </ul>
              </section>

              {/* Booking Terms */}
              <section>
                <h3 className="text-xl font-semibold text-[#ba1609] mb-3">Booking Terms</h3>
                <div className="space-y-3">
                  <div>
                    <h4 className="font-medium text-[#1a1012] mb-2">Reservations</h4>
                    <ul className="list-disc list-inside space-y-1 ml-4">
                      <li>All bookings must be confirmed in advance</li>
                      <li>Booking confirmation will be sent via email</li>
                      <li>Changes to bookings subject to availability</li>
                      <li>Advanced booking recommended for guaranteed availability</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-[#1a1012] mb-2">Payment</h4>
                    <ul className="list-disc list-inside space-y-1 ml-4">
                      <li>Payment can be made online or in cash</li>
                      <li>Credit cards accepted: Visa, Mastercard, American Express</li>
                      <li>Prices include all taxes and fees</li>
                      <li>Currency: Euros (€)</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* Cancellation Policy */}
              <section>
                <h3 className="text-xl font-semibold text-[#ba1609] mb-3">Cancellation Policy</h3>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong>24+ hours before pickup:</strong> Full refund available</li>
                  <li><strong>12-24 hours before pickup:</strong> 50% refund</li>
                  <li><strong>Less than 12 hours:</strong> No refund</li>
                  <li><strong>No-show:</strong> Full charge applies</li>
                  <li>Flight delays: Flexible rescheduling available</li>
                  <li>Weather cancellations: Full refund or rescheduling</li>
                </ul>
              </section>

              {/* Passenger Responsibilities */}
              <section>
                <h3 className="text-xl font-semibold text-[#ba1609] mb-3">Passenger Responsibilities</h3>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Arrive at pickup location on time</li>
                  <li>Provide accurate contact information</li>
                  <li>Respect the vehicle and driver</li>
                  <li>No smoking or eating in vehicles</li>
                  <li>Comply with Greek traffic laws</li>
                  <li>Report any issues immediately</li>
                </ul>
              </section>

              {/* Liability */}
              <section>
                <h3 className="text-xl font-semibold text-[#ba1609] mb-3">Liability and Insurance</h3>
                <p className="leading-relaxed mb-3">
                  <span style={{ fontFamily: 'Playfair Display, serif' }}>Lasmari Chauffeurs</span> maintains comprehensive insurance coverage for all vehicles and services. However:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>We are not liable for delays due to traffic, weather, or force majeure</li>
                  <li>Personal belongings are the passenger's responsibility</li>
                  <li>Medical conditions must be disclosed if relevant to transportation</li>
                  <li>Maximum liability limited to the booking value</li>
                </ul>
              </section>

              {/* Conduct Policy */}
              <section>
                <h3 className="text-xl font-semibold text-[#ba1609] mb-3">Passenger Conduct</h3>
                <p className="leading-relaxed mb-3">
                  We reserve the right to refuse service or terminate transportation for:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Intoxication or disruptive behavior</li>
                  <li>Damage to vehicle or equipment</li>
                  <li>Violation of Greek laws</li>
                  <li>Threatening or abusive behavior toward staff</li>
                  <li>Non-payment of agreed fees</li>
                </ul>
              </section>

              {/* Service Availability */}
              <section>
                <h3 className="text-xl font-semibold text-[#ba1609] mb-3">Service Availability</h3>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Services available 24/7 with advance booking</li>
                  <li>Seasonal variations may apply</li>
                  <li>Some remote locations may incur additional charges</li>
                  <li>Vehicle availability subject to fleet status</li>
                </ul>
              </section>

              {/* Governing Law */}
              <section>
                <h3 className="text-xl font-semibold text-[#ba1609] mb-3">Governing Law</h3>
                <p className="leading-relaxed">
                  These terms are governed by Greek law. Any disputes will be resolved in the courts of Corfu, Greece.
                </p>
              </section>

              {/* Contact */}
              <section>
                <h3 className="text-xl font-semibold text-[#ba1609] mb-3">Contact Information</h3>
                <p className="leading-relaxed mb-3">
                  For questions about these Terms of Service or our services:
                </p>
                <div className="space-y-1">
                  <p><strong>Email:</strong> contact@lasmari.gr</p>
                  <p><strong>Phone:</strong> +30 2663098633</p>
                  <p><strong>WhatsApp:</strong> +30 698 6666 768</p>
                  <p><strong>Address:</strong> Apraos, North Corfu, Greece</p>
                </div>
              </section>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default TermsOfServiceModal; 